import { Injectable, signal } from '@angular/core';
import {
  ActionStatus,
  ActionsState,
  ClientAction,
  Opportunity,
  OpportunityStatus,
  SourceType,
} from './client-actions.model';

function uuid(): string {
  return Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
}

type StoryOpportunitiesSource = 'delta' | 'snapshot' | 'root' | 'none';

function toRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined;
}

function findOpportunitiesContainer(
  section: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!section) return undefined;

  const preferred: Array<Record<string, unknown> | undefined> = [
    section,
    toRecord(section['copilot']),
    toRecord(section['fa.chat.thread']),
    toRecord(section['fa.chat']),
    toRecord(section['assistant.copilot']),
    toRecord(section['shared.metrics']),
    toRecord(section['shared']),
  ];

  for (const candidate of preferred) {
    if (candidate && Array.isArray(candidate['opportunities'])) {
      return candidate;
    }
  }

  // Fallback breadth-first scan up to a shallow depth to handle
  // mixed envelope shapes without over-traversing arbitrary objects.
  const seen = new Set<Record<string, unknown>>();
  let frontier: Record<string, unknown>[] = [section];
  let depth = 0;
  while (frontier.length > 0 && depth < 3) {
    const next: Record<string, unknown>[] = [];
    for (const obj of frontier) {
      if (seen.has(obj)) continue;
      seen.add(obj);
      if (Array.isArray(obj['opportunities'])) {
        return obj;
      }
      for (const value of Object.values(obj)) {
        const nested = toRecord(value);
        if (nested) next.push(nested);
      }
    }
    frontier = next;
    depth += 1;
  }
  return undefined;
}

function stepDemoId(stepKey: string): string {
  const idx = stepKey.indexOf(':');
  return idx >= 0 ? stepKey.slice(0, idx) : stepKey;
}

function stepNumber(stepKey: string): number | null {
  const idx = stepKey.indexOf(':');
  if (idx < 0 || idx >= stepKey.length - 1) return null;
  const raw = stepKey.slice(idx + 1).trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function normalizePriority(raw: unknown): Opportunity['priority'] {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'next' || value === 'urgent' || value === 'highest') return 'High';
  if (value.startsWith('high')) return 'High';
  if (value.startsWith('low')) return 'Low';
  return 'Medium';
}

function normalizeStatus(raw: unknown): OpportunityStatus {
  const value = String(raw ?? '').trim().toLowerCase();
  if (
    value === 'not started' ||
    value === 'new' ||
    value === 'to do' ||
    value === 'todo' ||
    value === 'open' ||
    value === 'pending'
  ) {
    return 'Potential';
  }
  if (value === 'potential') return 'Potential';
  if (value === 'committed' || value === 'in progress' || value === 'active') return 'Committed';
  if (value === 'completed' || value === 'done' || value === 'resolved') return 'Completed';
  if (value === 'withdrawn' || value === 'cancelled' || value === 'canceled') return 'Withdrawn';
  return 'Potential';
}

function normalizeSourceType(rawSourceType: unknown, rawSource: unknown): SourceType {
  const sourceType = String(rawSourceType ?? '').trim().toLowerCase();
  if (sourceType === 'human' || sourceType === 'fa') return 'human';
  if (sourceType === 'aiagent' || sourceType === 'ai' || sourceType === 'agent') return 'aiAgent';

  const source = String(rawSource ?? '').trim().toLowerCase();
  if (
    source === 'copilot' ||
    source === 'ai' ||
    source === 'agent' ||
    source === 'brightai analysis'
  ) {
    return 'aiAgent';
  }
  return 'human';
}

function normalizeDateToken(raw: unknown, fallback: string): string {
  const value = String(raw ?? '').trim();
  if (!value) return fallback;

  if (value.includes('{{today}}')) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const match = value.match(/\{\{today\}\}\s*([+-])\s*(\d+)/);
    if (match) {
      const sign = match[1];
      const days = Number(match[2]);
      const shift = sign === '+' ? days : -days;
      today.setDate(today.getDate() + shift);
    }
    return today.toISOString().slice(0, 10);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return parsed.toISOString().slice(0, 10);
}

function normalizeStoryOpportunity(
  raw: unknown,
  idx: number,
  todayIso: string
): Opportunity | null {
  const obj = toRecord(raw);
  if (!obj) return null;

  const description = String(obj['description'] ?? obj['title'] ?? '').trim();
  if (!description) return null;

  const idFromPayload = String(obj['id'] ?? '').trim();
  const id =
    idFromPayload.length > 0
      ? idFromPayload
      : `opp-${slugify(description)}-${idx + 1}`;
  const source = String(obj['source'] ?? 'Copilot').trim() || 'Copilot';
  const createdDate = normalizeDateToken(
    obj['createdDate'] ?? obj['createDate'],
    todayIso
  );
  const updatedDate = normalizeDateToken(obj['updatedDate'], createdDate);

  return {
    id,
    opportunityDescription: description,
    source,
    sourceType: normalizeSourceType(obj['sourceType'], source),
    priority: normalizePriority(obj['priority']),
    status: normalizeStatus(obj['status']),
    createdDate,
    updatedDate,
  };
}

function buildOpportunityFingerprint(
  stepKey: string,
  extracted: { source: StoryOpportunitiesSource; opportunities: Opportunity[] }
): string {
  const normalized = [...extracted.opportunities]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((o) =>
      [
        o.id,
        o.opportunityDescription,
        o.source,
        o.sourceType,
        o.priority,
        o.status,
        o.createdDate,
        o.updatedDate,
      ].join('|')
    )
    .join('||');
  return `${stepKey}::${extracted.source}::${normalized}`;
}

function extractStoryOpportunities(raw: unknown): {
  source: StoryOpportunitiesSource;
  opportunities: Opportunity[];
} {
  const payload = toRecord(raw);
  if (!payload) return { source: 'none', opportunities: [] };

  const deltaSection = toRecord(payload['delta']);
  const deltaContainer = findOpportunitiesContainer(deltaSection);
  if (deltaContainer) {
    const list = Array.isArray(deltaContainer['opportunities'])
      ? deltaContainer['opportunities']
      : [];
    const todayIso = new Date().toISOString().slice(0, 10);
    return {
      source: 'delta',
      opportunities: list
        .map((item, idx) => normalizeStoryOpportunity(item, idx, todayIso))
        .filter((o): o is Opportunity => o !== null),
    };
  }

  const snapshotSection = toRecord(payload['snapshot']);
  const snapshotContainer = findOpportunitiesContainer(snapshotSection);
  if (snapshotContainer) {
    const list = Array.isArray(snapshotContainer['opportunities'])
      ? snapshotContainer['opportunities']
      : [];
    const todayIso = new Date().toISOString().slice(0, 10);
    return {
      source: 'snapshot',
      opportunities: list
        .map((item, idx) => normalizeStoryOpportunity(item, idx, todayIso))
        .filter((o): o is Opportunity => o !== null),
    };
  }

  const rootContainer = findOpportunitiesContainer(payload);
  if (rootContainer) {
    const list = Array.isArray(rootContainer['opportunities'])
      ? rootContainer['opportunities']
      : [];
    const todayIso = new Date().toISOString().slice(0, 10);
    return {
      source: 'root',
      opportunities: list
        .map((item, idx) => normalizeStoryOpportunity(item, idx, todayIso))
        .filter((o): o is Opportunity => o !== null),
    };
  }

  return { source: 'none', opportunities: [] };
}

function seedOpportunities(): Opportunity[] {
  return [
    {
      id: uuid(),
      opportunityDescription: 'Rollover IRA from previous employer 401(k)',
      source: 'BrightAI Analysis',
      sourceType: 'aiAgent',
      createdDate: '2025-11-01',
      updatedDate: '2025-11-01',
      priority: 'High',
      status: 'Potential',
    },
    {
      id: uuid(),
      opportunityDescription: 'Review term life insurance coverage gap',
      source: 'Annual Review',
      sourceType: 'aiAgent',
      createdDate: '2025-10-15',
      updatedDate: '2025-10-20',
      priority: 'Medium',
      status: 'Potential',
    },
    {
      id: uuid(),
      opportunityDescription: 'Explore 529 plan for college savings',
      source: 'Client Meeting',
      sourceType: 'human',
      createdDate: '2025-09-10',
      updatedDate: '2025-09-10',
      priority: 'Low',
      status: 'Committed',
    },
  ];
}

function seedActions(): ClientAction[] {
  return [
    {
      id: uuid(),
      financialAssistantName: 'Sarah Chen',
      action: 'Send updated estate planning checklist',
      opportunityId: null,
      source: 'BrightAI Analysis',
      sourceType: 'aiAgent',
      createdDate: '2025-11-05',
      plannedDate: '2025-11-12',
      deadline: '2025-11-30',
      status: 'To Do',
    },
    {
      id: uuid(),
      financialAssistantName: 'Sarah Chen',
      action: 'Schedule quarterly portfolio review call',
      opportunityId: null,
      source: 'Manual',
      sourceType: 'human',
      createdDate: '2025-10-28',
      plannedDate: '2025-11-15',
      deadline: '2025-12-01',
      status: 'In Progress',
    },
  ];
}

function emptyState(): ActionsState {
  return {
    opportunities: seedOpportunities(),
    actions: seedActions(),
  };
}

/**
 * Scoped state service for the Actions tab.
 * Provided at MyClientsLayoutComponent level (not root) so state is
 * isolated per layout instance and cleared on navigation away.
 */
@Injectable()
export class ClientActionsStateService {
  private _store = signal<Record<string, ActionsState>>({});
  private readonly _activeStepKeyBySession = signal<Record<string, string>>({});
  private readonly _appliedFingerprintBySession = signal<Record<string, string>>({});
  private readonly _storyRevisionBySession = signal<Record<string, number>>({});
  private readonly _fallbackOpportunitiesBySession = signal<Record<string, Opportunity[]>>({});

  /** Read-only snapshot of the store for reactive computed() bindings in components. */
  readonly storeSnapshot = this._store.asReadonly();

  /**
   * Returns state for the given session. If no state exists yet, seeds it.
   * NOTE: Do not call from inside a computed() — use ensureState() first then read storeSnapshot.
   */
  getState(sessionId: string): ActionsState {
    const store = this._store();
    return store[sessionId] ?? this._seed(sessionId);
  }

  /** Ensures state exists for the session without returning it (safe to call from effects). */
  ensureState(sessionId: string): void {
    if (!this._store()[sessionId]) {
      this._seed(sessionId);
    }
    if (!(sessionId in this._activeStepKeyBySession())) {
      this._activeStepKeyBySession.update((prev) => ({
        ...prev,
        [sessionId]: '',
      }));
    }
    if (!(sessionId in this._appliedFingerprintBySession())) {
      this._appliedFingerprintBySession.update((prev) => ({
        ...prev,
        [sessionId]: '',
      }));
    }
    if (!(sessionId in this._storyRevisionBySession())) {
      this._storyRevisionBySession.update((prev) => ({
        ...prev,
        [sessionId]: 0,
      }));
    }
  }

  private _seed(sessionId: string): ActionsState {
    const seeded = emptyState();
    this._store.update((s) => ({ ...s, [sessionId]: seeded }));
    return seeded;
  }

  private _updateOpportunity(
    sessionId: string,
    id: string,
    patch: Partial<Opportunity>
  ): void {
    this._store.update((store) => {
      const state = store[sessionId] ?? emptyState();
      return {
        ...store,
        [sessionId]: {
          ...state,
          opportunities: state.opportunities.map((o) =>
            o.id === id ? { ...o, ...patch, updatedDate: new Date().toISOString().slice(0, 10) } : o
          ),
        },
      };
    });
  }

  private _updateAction(
    sessionId: string,
    id: string,
    patch: Partial<ClientAction>
  ): void {
    this._store.update((store) => {
      const state = store[sessionId] ?? emptyState();
      return {
        ...store,
        [sessionId]: {
          ...state,
          actions: state.actions.map((a) =>
            a.id === id ? { ...a, ...patch } : a
          ),
        },
      };
    });
  }

  /** Approve an AI-generated opportunity: transitions Potential → Committed. No-op otherwise. */
  approveOpportunity(sessionId: string, id: string): void {
    const state = this._store()[sessionId];
    if (!state) return;
    const opp = state.opportunities.find((o) => o.id === id);
    if (!opp || opp.status !== 'Potential') return;
    this._updateOpportunity(sessionId, id, { status: 'Committed' });
  }

  /** Withdraw an opportunity: sets status to Withdrawn (row preserved). */
  withdrawOpportunity(sessionId: string, id: string): void {
    this._updateOpportunity(sessionId, id, { status: 'Withdrawn' });
  }

  /** General opportunity status update. */
  updateOpportunityStatus(sessionId: string, id: string, status: OpportunityStatus): void {
    this._updateOpportunity(sessionId, id, { status });
  }

  /** Approve an AI-generated action: sets status to To Do (activates it). */
  approveAction(sessionId: string, id: string): void {
    this._updateAction(sessionId, id, { status: 'To Do' });
  }

  /** Withdraw an action: sets status to Withdrawn (row preserved). */
  withdrawAction(sessionId: string, id: string): void {
    this._updateAction(sessionId, id, { status: 'Withdrawn' });
  }

  /** General action status update. */
  updateActionStatus(sessionId: string, id: string, status: ActionStatus): void {
    this._updateAction(sessionId, id, { status });
  }

  updateOpportunity(sessionId: string, id: string, patch: Partial<Opportunity>): void {
    this._updateOpportunity(sessionId, id, patch);
  }

  updateAction(sessionId: string, id: string, patch: Partial<ClientAction>): void {
    this._updateAction(sessionId, id, patch);
  }

  addOpportunity(sessionId: string, opp: Omit<Opportunity, 'id'>): void {
    const newOpp: Opportunity = { ...opp, id: uuid() };
    this._store.update((store) => {
      const state = store[sessionId] ?? emptyState();
      return {
        ...store,
        [sessionId]: {
          ...state,
          opportunities: [...state.opportunities, newOpp],
        },
      };
    });
  }

  addAction(sessionId: string, action: Omit<ClientAction, 'id'>): void {
    const newAction: ClientAction = { ...action, id: uuid() };
    this._store.update((store) => {
      const state = store[sessionId] ?? emptyState();
      return {
        ...store,
        [sessionId]: {
          ...state,
          actions: [...state.actions, newAction],
        },
      };
    });
  }

  /**
   * Story Service integration for the Opportunities grid.
   * For a single story run, opportunities are accumulated by id as steps progress.
   * On demo switch or rewind/reset, accumulation starts fresh.
   */
  applyStepPayload(sessionId: string, raw: unknown, stepKey: string): void {
    this.ensureState(sessionId);

    const extracted = extractStoryOpportunities(raw);
    const fingerprint = buildOpportunityFingerprint(stepKey, extracted);
    const currentStepKey = this._activeStepKeyBySession()[sessionId] ?? '';
    const previousFingerprint = this._appliedFingerprintBySession()[sessionId] ?? '';
    if (currentStepKey === stepKey && previousFingerprint === fingerprint) return;
    const current = this._store()[sessionId] ?? emptyState();
    const existingFallbackBaseline = this._fallbackOpportunitiesBySession()[sessionId];

    // Entering story mode from fallback/default: capture current opportunities so
    // leaving story mode can restore the same non-story view.
    if (
      (currentStepKey === '' || currentStepKey === 'fallback') &&
      !existingFallbackBaseline
    ) {
      this._fallbackOpportunitiesBySession.update((prev) => ({
        ...prev,
        [sessionId]: current.opportunities.map((opp) => ({ ...opp })),
      }));
    }

    const previousDemoId = stepDemoId(currentStepKey);
    const nextDemoId = stepDemoId(stepKey);
    const previousStep = stepNumber(currentStepKey);
    const nextStep = stepNumber(stepKey);
    const isNonForwardStep =
      previousStep !== null && nextStep !== null && nextStep <= previousStep;
    const startsFreshStoryRun =
      currentStepKey === '' ||
      currentStepKey === 'fallback' ||
      previousDemoId !== nextDemoId ||
      isNonForwardStep;

    if (extracted.source === 'none') {
      // On rewind/reset/new demo with no opportunities payload yet, clear run accumulation.
      if (startsFreshStoryRun) {
        this._store.update((store) => ({
          ...store,
          [sessionId]: {
            ...current,
            opportunities: [],
          },
        }));
      }
      this._activeStepKeyBySession.update((prev) => ({
        ...prev,
        [sessionId]: stepKey,
      }));
      this._appliedFingerprintBySession.update((prev) => ({
        ...prev,
        [sessionId]: fingerprint,
      }));
      return;
    }

    let nextOpportunities: Opportunity[];
    if (extracted.source === 'delta') {
      const baseline = startsFreshStoryRun ? [] : current.opportunities;
      const mergedById = new Map<string, Opportunity>(
        baseline.map((opp) => [opp.id, opp])
      );
      for (const incoming of extracted.opportunities) {
        mergedById.set(incoming.id, incoming);
      }
      nextOpportunities = [...mergedById.values()];
    } else {
      // Snapshot/root payloads are treated as authoritative state for the current step.
      nextOpportunities = extracted.opportunities;
    }

    const existingIds = new Set(current.opportunities.map((opp) => opp.id));
    const newCount = nextOpportunities.reduce(
      (count, opp) => (existingIds.has(opp.id) ? count : count + 1),
      0
    );

    this._store.update((store) => ({
      ...store,
      [sessionId]: {
        ...current,
        opportunities: nextOpportunities,
      },
    }));
    this._activeStepKeyBySession.update((prev) => ({
      ...prev,
      [sessionId]: stepKey,
    }));
    this._appliedFingerprintBySession.update((prev) => ({
      ...prev,
      [sessionId]: fingerprint,
    }));
    if (newCount > 0) {
      this._storyRevisionBySession.update((prev) => ({
        ...prev,
        [sessionId]: (prev[sessionId] ?? 0) + newCount,
      }));
    }
  }

  loadFallback(sessionId: string): void {
    this.ensureState(sessionId);
    const fallbackBaseline = this._fallbackOpportunitiesBySession()[sessionId];
    if (fallbackBaseline) {
      const current = this._store()[sessionId] ?? emptyState();
      this._store.update((store) => ({
        ...store,
        [sessionId]: {
          ...current,
          opportunities: fallbackBaseline.map((opp) => ({ ...opp })),
        },
      }));
      this._fallbackOpportunitiesBySession.update((prev) => {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
    }
    this._activeStepKeyBySession.update((prev) => ({
      ...prev,
      [sessionId]: 'fallback',
    }));
    this._appliedFingerprintBySession.update((prev) => ({
      ...prev,
      [sessionId]: 'fallback',
    }));
  }

  storyRevision(sessionId: string): number {
    return this._storyRevisionBySession()[sessionId] ?? 0;
  }
}
